import React from 'react'

import styled from '@emotion/styled'

import { border, spacing } from '../../theme'
import { ArcadeColors, useGetArcadeColor } from '../../theme/arcadeColors'

const isSpacing = (space: keyof typeof spacing | string): space is keyof typeof spacing =>
  (space as keyof typeof spacing) in spacing

const parsePadding = (padding: string) =>
  padding
    .split(/\s/)
    .map(p => (isSpacing(p) ? spacing[p] : p))
    .join(' ')

const isBorderRadius = (radius: keyof typeof border.radius | string): radius is keyof typeof border.radius =>
  (radius as keyof typeof border.radius) in border.radius

const parseBorderRadius = (borderRadius: string) => {
  // Check if it's a border radius token and resolve it
  if (isBorderRadius(borderRadius)) {
    return border.radius[borderRadius]
  }
  // Otherwise return the custom value as-is
  return borderRadius
}

export interface ArcadeBoxProps {
  /** Background color token */
  backgroundColor?: ArcadeColors
  /** CSS border styling (e.g., "1px solid #ccc") */
  border?: string
  /** Border radius using theme tokens or custom values (e.g., "small", "medium", "8px") */
  borderRadius?: keyof typeof border.radius | string
  /** Whether the box should take full height */
  fullHeight?: boolean
  /**
   * Padding using theme spacing tokens or custom values
   * @example "m" | "l xl" | "s m l xl" | "1rem 2rem" | "m 2rem s 1rem"
   */
  padding?: keyof typeof spacing | string
}

const StyledArcadeBox = styled.div<ArcadeBoxProps & { backgroundColorHex?: string }>`
  ${({ backgroundColorHex }) => backgroundColorHex && `background-color: ${backgroundColorHex};`}
  ${({ border }) => border && `border: ${border};`}
  ${({ borderRadius }) => borderRadius && `border-radius: ${parseBorderRadius(borderRadius)};`}
  ${({ fullHeight }) => fullHeight && `min-height: 100%;`}
  ${({ padding }) => padding && `padding: ${parsePadding(padding)};`}
`

export const ArcadeBox = React.forwardRef<HTMLDivElement, ArcadeBoxProps & React.HTMLAttributes<HTMLDivElement>>(
  ({ backgroundColor, ...props }, ref) => {
    const backgroundColorHex = useGetArcadeColor(backgroundColor)
    return <StyledArcadeBox ref={ref} backgroundColorHex={backgroundColorHex} {...props} />
  }
)

export default ArcadeBox
